<template>
  <div class="documents col-xl-5 mx-auto">
    <h5 class="mt-4"><b>Documents</b></h5>
    <p v-if="!documents.length">You don't have any documents. Click the button below to add one.</p>
    <div v-else>
      <b-table striped :items="documents" :fields="fields" class="table-light">
        <template slot="actions" slot-scope="data">
          <b-button
            size="sm"
            download
            @click="downloadDocument(data.item.documentID)"
            variant="success">View</b-button>
          <b-button 
            @click.prevent.stop="deleteClicked(data.item.documentID)" 
            variant="danger" 
            size="sm" 
            class="ml-1"
            v-if="!isViewOnly">Delete</b-button>
        </template>
      </b-table>
    </div>

    <b-alert show variant="danger" v-if="error" class="text-left mt-3">
      <p class="mb-0">Could not download the document.</p>
    </b-alert>

    <b-button @click="showDocumentModal" variant="success" v-if="!isViewOnly">Add Document</b-button>
    <b-alert show variant="danger" v-if="documentErrors.length" class="mt-5 text-left">
      <h4 class="mb-0">Error{{ documentErrors.length == 1 ? '' : 's' }}</h4>
      <hr class="mt-1">
      <ul>
        <li v-for="(de, i) in documentErrors" :key="i">
          {{ de }}
        </li>
      </ul>
    </b-alert>

    <b-modal ref="document-modal" title="Add a new document" header-text-variant="dark" body-text-variant="dark" centered>
      <template slot="default">
        <b-form-group label="File" :state="IsFieldValid($v.document.file)" :invalid-feedback="FileMessage($v.document.file)">
          <b-form-file accept=".pdf, .txt, .doc, .docx, .xls, .xlsx, .jpg, .jpeg, .png" v-model="$v.document.file.$model" :state="IsFieldValid($v.document.file)" placeholder="Choose a file..." drop-placeholder="Drop file here..."></b-form-file>
        </b-form-group>
        <b-form-group label="Description" :state="IsFieldValid($v.document.description)" :invalid-feedback="RequiredAndMaxLengthMessage('Description', $v.document.description)">
          <b-form-textarea v-model="$v.document.description.$model" rows="3" placeholder="Describe this document..." :state="IsFieldValid($v.document.description)"></b-form-textarea>
        </b-form-group>
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="danger" @click="hideDocumentModal">Cancel</b-button>
        <b-button variant="success" @click="uploadClicked" class="ml-1">Upload Document</b-button>
      </template>
    </b-modal>
    
    <b-modal ref="delete-modal" title="Are you sure?" header-text-variant="dark" body-text-variant="dark" centered>
      <template slot="default">
        Are you sure you want to delete this document?
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="danger" @click.prevent="deleteDocument" :disabled="isDeleting">
          <span v-if="!isDeleting">Delete</span>
          <span v-else><b-spinner small></b-spinner> Please Wait...</span>
        </b-button>
        <b-button variant="secondary" @click.prevent="cancelClicked">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { required, maxLength } from 'vuelidate/lib/validators'
import Document from '../models/Document.js'
import DocumentService from '../services/DocumentService.js'
import BaseService from '../services/BaseService.js'
import CustomValidations from '../mixins/customValidations.js'
const fileTypeValidation = CustomValidations.fileTypeValidation

export default {
  name: 'Documents',
  data() {
    return {
      error: false,
      document: '',
      documentErrors: [],
      fields: [
        { key: 'fileName' , label: 'File Name', sortable: true, thStyle: { width: '30%' } },
        { key: 'description' , label: 'Description', sortable: true, thStyle: { width: '40%' } },
        { key: 'created' , label: 'Created', sortable: true, thStyle: { width: '15%' }, formatter: this.DateFormatter },
        { key: 'actions', label: '', sortable: false, class: ['action-buttons'] }
      ],
      documentIdToDelete: '',
      isDeleting: false
    }
  },
  validations: {
    document: {
      file: {
        required,
        fileTypeValidation
      },
      description: {
        required,
        maxLength: maxLength(100)
      }
    }
  },
  methods: {
    downloadDocument(id) {
      this.error = false
      BaseService.getFileDownload(`/Document/${id}`).then(response => {
        this.DownloadFile(response)
      }).catch(() => {
        this.error = true
      })
    },
    showDocumentModal() {
      this.$v.$reset()
      this.$refs['document-modal'].show()
    },
    hideDocumentModal() {
      this.$refs['document-modal'].hide()
    },
    uploadClicked() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.$store.dispatch('document/save', this.document).then(() => {
        this.hideDocumentModal()
        this.document = new Document(this.change.changeID)
      })
    },
    deleteClicked(id) {
      this.$refs['delete-modal'].show()
      this.documentIdToDelete = id
    },
    cancelClicked() {
      this.$refs['delete-modal'].hide()
      this.documentIdToDelete = ''
    },
    deleteDocument() {
      this.isDeleting = true
      this.$store.dispatch('document/delete', this.documentIdToDelete).then(() => {
        this.isDeleting = false
        this.$refs['delete-modal'].hide()
        this.documentIdToDelete = ''
      }).catch(() => {
        this.isDeleting = false
      })
    },
    backClicked() {
      this.$router.push({
        name: 'questions',
        params: {
          submissionId: this.change.submissionID,
          changeId: this.change.changeID
        }
      })
    },
    nextClicked() {
      if (this.isViewOnly) {
        this.$router.push({
          name: 'change/list',
          params: {
            submissionId: this.change.submissionID
          }
        })
      } else {        
        DocumentService.validate(this.change.changeID).then(() => {
          this.$router.push({
            name: 'change/list',
            params: {
              submissionId: this.change.submissionID
            }
          })
        }).catch(res => {
          this.documentErrors = res.response.data
        })
      }
    }
  },
  computed: {
    ...mapGetters({
      documents: "document/documentsForChange",
      isViewOnly: 'application/isChangeViewOnly'
    }),
    ...mapState({
      change: state => state.change.change
    })
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
    this.$EventBus.$on('next-clicked', this.nextClicked)
    this.document = new Document(this.change.changeID)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
    this.$EventBus.$off('next-clicked', this.nextClicked)
  }
}
</script>